// AdminUsers.jsx — ARQUIVO COMPLETO
import React, { useEffect, useState, useCallback } from "react";
import {
  Search,
  User,
  Mail,
  Phone,
  Calendar,
  AlertCircle,
  Shield,
  Crown,
  Trash2,
  UserX,
  UserCheck,
  Building2,
  Briefcase,
  Gift,
  Pencil,
  X
} from "lucide-react";
import adminService from "../../../../services/adminService";
import "./Adminusers.scss";

const FILTERS = [
  { key: "todos", label: "Todos" },
  { key: "assinantes", label: "Assinantes" },
  { key: "bloqueados", label: "Bloqueados" },
];

const normalizePlan = (isGold) => (isGold ? "GOLD" : "FREE");

const PLAN_ICONS = { FREE: Shield, GOLD: Crown };
const PLAN_LABELS = { FREE: "Free", GOLD: "Gold" };

export default function AdminUsers({ userType, title, onChanged }) {
  const [filter, setFilter] = useState("todos");
  const [search, setSearch] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", cpf: "", cnpj: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");

  const isCompanies = userType === "companies";

  const api = isCompanies ? {
    list: adminService.getCompanies,
    block: adminService.toggleBlockCompany,
    remove: adminService.deleteCompany,
  } : {
    list: adminService.getFreelancers,
    plan: adminService.updateFreelancerPlan,
    block: adminService.toggleBlockFreelancer,
    remove: adminService.deleteFreelancer,
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.list("todos", search);
      setAllUsers(data);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    } finally {
      setLoading(false);
    }
  }, [search, userType]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePlanChange = async (user) => {
    const wantsGold = !user.isGold;
    const expiresAt = wantsGold ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() : null;

    try {
      await adminService.updateGold(user.id, wantsGold, expiresAt);
      await load();
      onChanged?.();
    } catch (error) {
      alert("Erro ao alterar plano. Tente novamente.");
    }
  };

  const handleActivateGoldForAll = async () => {
    if (!window.confirm(
      "Isso vai ativar 7 dias de Gold para TODOS os freelancers que ainda não têm Gold ativo agora. Confirma?"
    )) return;

    try {
      const result = await adminService.activateGoldForAllFreelancers();
      alert(result.message);
      await load();
      onChanged?.();
    } catch (error) {
      alert(error?.response?.data?.message || "Erro ao ativar Gold em massa. Tente novamente.");
    }
  };

  const handleDeactivateGoldForAll = async () => {
    if (!window.confirm(
      "Isso vai DESATIVAR o Gold de TODOS os freelancers com assinatura ativa agora, imediatamente. Confirma?"
    )) return;

    try {
      const result = await adminService.deactivateGoldForAllFreelancers();
      alert(result.message);
      await load();
      onChanged?.();
    } catch (error) {
      alert(error?.response?.data?.message || "Erro ao desativar Gold em massa. Tente novamente.");
    }
  };

  const handleGrantGold = async (user) => {
    if (!window.confirm(`Conceder 30 dias de Gold gratuito para "${user.name}"?`)) return;

    try {
      const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      await adminService.updateGold(user.id, true, expiresAt);
      await load();
      onChanged?.();
    } catch (error) {
      alert("Erro ao conceder Gold. Tente novamente.");
    }
  };

  const handleBlock = async (user) => {
    try {
      await api.block(user.id, !user.isBanned);
      await load();
      onChanged?.();
    } catch (error) {
      alert("Erro ao alterar status. Tente novamente.");
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${user.name}" permanentemente?`)) return;
    try {
      await api.remove(user.id);
      await load();
      onChanged?.();
    } catch (error) {
      alert("Erro ao excluir usuário. Tente novamente.");
    }
  };

  // ─── Edição de dados cadastrais (NOVO) ─────────────────────────────
  const openEdit = (user) => {
    setEditError("");
    setEditingUser(user);
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      cpf: user.cpf || "",
      cnpj: user.cnpj || "",
    });
  };

  const closeEdit = () => {
    if (savingEdit) return;
    setEditingUser(null);
  };

  const handleEditFieldChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    if (!editForm.name.trim() || !editForm.email.trim()) {
      setEditError("Nome e e-mail são obrigatórios.");
      return;
    }

    setSavingEdit(true);
    setEditError("");

    try {
      await adminService.updateUserDetails(editingUser.id, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim() || null,
        cpf: isCompanies ? null : (editForm.cpf.trim() || null),
        cnpj: isCompanies ? (editForm.cnpj.trim() || null) : null,
      });
      setEditingUser(null);
      await load();
      onChanged?.();
    } catch (error) {
      setEditError(error?.response?.data?.message || "Erro ao salvar. Tente novamente.");
    } finally {
      setSavingEdit(false);
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "?";
  };

  const formatExpires = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getFilteredCount = (filterKey) => {
    if (filterKey === "todos") return allUsers.length;
    if (filterKey === "assinantes") return allUsers.filter(u => u.isGold).length;
    if (filterKey === "bloqueados") return allUsers.filter(u => u.isBanned).length;
    return 0;
  };

  const displayUsers = allUsers.filter(u => {
    if (filter === "assinantes") return u.isGold;
    if (filter === "bloqueados") return u.isBanned;
    return true;
  });

  return (
    <div className="admin-users">
      <div className="admin-users__header">
        <div className="admin-users__title">
          {isCompanies ? <Building2 size={20} /> : <Briefcase size={20} />}
          {title}
        </div>

        {!isCompanies && (
          <>
            <button
              className="admin-users__btn admin-users__btn--gold-grant"
              onClick={handleActivateGoldForAll}
              title="Ativar 7 dias de Gold para todos os freelancers"
            >
              <Gift size={14} />
              Ativar 7 dias para todos
            </button>
            <button
              className="admin-users__btn admin-users__btn--block"
              onClick={handleDeactivateGoldForAll}
              title="Desativar o Gold de todos os freelancers"
            >
              <UserX size={14} />
              Desativar para todos
            </button>
          </>
        )}

        <span className="admin-users__count">
          Total <span>{displayUsers.length}</span>
        </span>
      </div>
      <div className="admin-users__toolbar">
        <div className="admin-users__tabs">
          {FILTERS.map((f) => {
            const count = getFilteredCount(f.key);
            return (
              <button
                key={f.key}
                className={filter === f.key ? "active" : ""}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
                <span className="count">({count})</span>
              </button>
            );
          })}
        </div>

        <div className="admin-users__search">
          <Search size={16} className="admin-users__search__icon" />
          <input
            placeholder="Buscar por nome, email, telefone, CPF/CNPJ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading && (
        <div className="admin-users__loading">
          Carregando usuários...
        </div>
      )}

      {!loading && displayUsers.length > 0 && (
        <div className="admin-users__list">
          {displayUsers.map((u) => {
            const planKey = normalizePlan(u.isGold);
            const planLabel = PLAN_LABELS[planKey];
            const PlanIcon = PLAN_ICONS[planKey];

            return (
              <div
                className={`admin-users__item ${u.isBanned ? "admin-users__item--blocked" : ""}`}
                key={u.id}
              >
                <div className={`admin-users__item-avatar ${u.isBanned ? "admin-users__item-avatar--blocked" : ""}`}>
                  {getInitials(u.name)}
                </div>

                <div className="admin-users__item-info">
                  <div className="admin-users__item-top">
                    <h4 className="admin-users__item-name">{u.name}</h4>
                    <span className={`admin-users__item-badge admin-users__item-badge--${planKey.toLowerCase()}`}>
                      <PlanIcon size={12} />
                      {planLabel}
                    </span>
                    {u.isBanned && (
                      <span className="admin-users__item-badge admin-users__item-badge--blocked">
                        <UserX size={12} />
                        Bloqueado
                      </span>
                    )}
                  </div>

                  <div className="admin-users__item-meta">
                    <span>
                      <Mail size={14} />
                      {u.email}
                    </span>
                    <span>•</span>
                    <span>
                      <Phone size={14} />
                      {u.phone || "N/A"}
                    </span>
                    <span>•</span>
                    <span>
                      <User size={14} />
                      {u.cnpj || u.cpf || "N/A"}
                    </span>
                  </div>

                  {u.goldExpiresAt && u.isGold && (
                    <div className="admin-users__item-expires">
                      <Calendar size={14} />
                      Gold expira em: {formatExpires(u.goldExpiresAt)}
                    </div>
                  )}
                </div>

                <div className="admin-users__item-actions">
                  <button
                    className="admin-users__btn admin-users__btn--edit"
                    onClick={() => openEdit(u)}
                    title="Editar dados cadastrais"
                  >
                    <Pencil size={14} />
                    Editar
                  </button>

                  {isCompanies ? (
                    <button
                      className="admin-users__btn admin-users__btn--gold-grant"
                      onClick={() => handleGrantGold(u)}
                      title="Conceder 30 dias de Gold gratuito"
                    >
                      <Gift size={14} />
                      30 dias Gold
                    </button>
                  ) : (
                    <button
                      className="admin-users__btn admin-users__btn--plan"
                      onClick={() => handlePlanChange(u)}
                      title="Alternar plano (FREE <-> GOLD)"
                    >
                      <Shield size={14} />
                      Plano
                    </button>
                  )}
                  <button
                    className={`admin-users__btn ${u.isBanned ? "admin-users__btn--block--unblock" : "admin-users__btn--block"}`}
                    onClick={() => handleBlock(u)}
                  >
                    {u.isBanned ? (
                      <>
                        <UserCheck size={14} />
                        Desbloquear
                      </>
                    ) : (
                      <>
                        <UserX size={14} />
                        Bloquear
                      </>
                    )}
                  </button>
                  <button
                    className="admin-users__btn admin-users__btn--delete"
                    onClick={() => handleDelete(u)}
                  >
                    <Trash2 size={14} />
                    Excluir
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && displayUsers.length === 0 && (
        <div className="admin-users__empty">
          <AlertCircle size={40} />
          <h3>Nenhum usuário encontrado</h3>
          <p>
            {search
              ? "Nenhum resultado encontrado para sua busca."
              : filter === "assinantes"
                ? "Não há usuários com Gold ativo."
                : filter === "bloqueados"
                  ? "Não há usuários bloqueados."
                  : "Não há usuários cadastrados nesta categoria."}
          </p>
        </div>
      )}

      {editingUser && (
        <div className="admin-users__modal-overlay" onClick={closeEdit}>
          <div className="admin-users__modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-users__modal-header">
              <h3>Editar dados de {editingUser.name}</h3>
              <button className="admin-users__modal-close" onClick={closeEdit}>
                <X size={18} />
              </button>
            </div>

            <div className="admin-users__modal-body">
              <label>
                Nome
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => handleEditFieldChange("name", e.target.value)}
                />
              </label>

              <label>
                E-mail
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => handleEditFieldChange("email", e.target.value)}
                />
              </label>

              <label>
                Telefone
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={(e) => handleEditFieldChange("phone", e.target.value)}
                  placeholder="(35) 90000-0000"
                />
              </label>

              <label>
                {isCompanies ? "CNPJ" : "CPF"}
                <input
                  type="text"
                  value={isCompanies ? editForm.cnpj : editForm.cpf}
                  onChange={(e) =>
                    handleEditFieldChange(isCompanies ? "cnpj" : "cpf", e.target.value)
                  }
                />
              </label>

              {editError && <div className="admin-users__modal-error">{editError}</div>}
            </div>

            <div className="admin-users__modal-footer">
              <button
                className="admin-users__btn admin-users__btn--block"
                onClick={closeEdit}
                disabled={savingEdit}
              >
                Cancelar
              </button>
              <button
                className="admin-users__btn admin-users__btn--plan"
                onClick={handleSaveEdit}
                disabled={savingEdit}
              >
                {savingEdit ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}